<template>
	<section
		v-if="items.length"
		class="solution-service"
		:class="{ 'solution-service--area': isAreaTeacher }"
	>
		<h3 class="section-title">{{ t(`${I18N_NS}.sections.solutionService`) }}</h3>
		<ul class="solution-service__grid" role="list">
			<li
				v-for="(item, index) in items"
				:key="item.id"
				:ref="el => bindSolutionImpression(el, item, index)"
				class="solution-service-card"
				role="listitem"
				@click="toServiceDetail(item, index)"
			>
				<img
					v-if="isAreaTeacher"
					class="solution-service-card__poster"
					:src="item.cover || DefaultImg"
					:alt="item.name"
				/>

				<img
					v-else
					class="solution-service-card__image"
					:src="item.cover || DefaultImg"
					:alt="item.name"
				/>

				<div v-if="!isAreaTeacher" class="solution-service-card__content">
					<header>
						<div class="solution-service-card__name">{{ item.name }}</div>
					</header>
					<p class="solution-service-card__detail" v-text="item.detail"></p>

					<p v-if="item.discount_tag" class="solution-service-card__original">
						{{ t(`${I18N_NS}.originalPrice`, { price: item.price }) }}
					</p>

					<footer class="solution-service-card__footer">
						<div class="solution-service-card__price">
							<img src="~/assets/images/pc/coin.png" :alt="t(`${I18N_NS}.coinAlt`)" />
							<span>{{ item.final_price }}</span>
						</div>
						<button
							type="button"
							class="solution-service-card__btn"
							:aria-label="t(`${I18N_NS}.serviceOrder`)"
							@click.stop="order(item, index)"
						>
							<span class="solution-service-card__plus" aria-hidden="true"></span>
						</button>
					</footer>
				</div>
			</li>
		</ul>
	</section>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, ref } from "vue";
import DefaultImg from "~/assets/images/default.png";
import { orderConsultant } from "~/api/api";
import { PayBox } from "~/hooks/pay/index";
import { useI18n } from "~/composables/useI18n";
import { useInjectedMentorDetailTracking } from "@variants/seer/teacher-detail/composables/useMentorDetailTracking";

const I18N_NS = "teacherDetail.pc";
const { t, locale } = useI18n();
const openLogin = inject("openLogin");
const tracking = useInjectedMentorDetailTracking();
const impressionTracker = tracking?.createImpressionTracker?.();

const props = defineProps({
	data: {
		type: Array,
		default: () => [],
	},
	variant: {
		type: String,
		default: "default",
	},
});

const route = useRoute();
const isOrder = ref(false);
const isAreaTeacher = computed(() => props.variant === "area");
const items = computed(() => (Array.isArray(props.data) ? props.data.map(formatServiceItem) : []));

function bindSolutionImpression(el, item, index) {
	if (!el || !impressionTracker || !item?.id) return;
	impressionTracker.bind(el, `solution_${item.id}`, "mentor_solution_card_impression", {
		solution_id: String(item.id),
		price_bucket: tracking.getPriceBucket(item.final_price ?? item.price),
		position: index + 1,
	});
}

function formatServiceItem(item) {
	return {
		...item,
		name: pickLocalized(item, "name"),
		detail: pickLocalizedDetail(item),
		cover: pickServiceImage(item),
	};
}

function pickLocalized(item, key) {
	const isEn = locale.value === "en";
	return (isEn ? item?.[`${key}_en`] : item?.[key]) || item?.[key] || "";
}

function pickLocalizedDetail(item) {
	if (locale.value === "en") {
		return item?.detail_en || item?.detail || "";
	}
	return item?.detail || "";
}

function pickServiceImage(item) {
	const candidates =
		locale.value === "en"
			? [item?.image, item?.background_image, item?.image_jt]
			: [item?.image_jt, item?.image, item?.background_image];
	return candidates.map(getFirstImageUrl).find(Boolean) || "";
}

function getFirstImageUrl(value) {
	if (Array.isArray(value)) {
		return value.map(getFirstImageUrl).find(Boolean) || "";
	}
	if (typeof value !== "string") return "";
	return value
		.split(",")
		.map(item => item.trim())
		.find(Boolean);
}

const toServiceDetail = async (data, index) => {
	tracking?.track?.("mentor_solution_add_click", {
		solution_id: String(data.id),
		price_bucket: tracking.getPriceBucket(data.final_price ?? data.price),
		position: typeof index === "number" ? index + 1 : undefined,
		action_type: "card",
	});
	await navigateTo(`/seer/teacher-service/${route.params.id}/${data.id}`);
};

const order = async (data, index) => {
	openLogin?.();
	if (isOrder.value) return;
	isOrder.value = true;

	try {
		const res = await orderConsultant({
			teacher_uid: route.params.id,
			service_id: data.id,
		});

		if (import.meta.client) {
			const { logEvent } = await import("~/utils/firebase");
			logEvent("site_masterpage_pay");
		}
		tracking?.track?.("mentor_solution_add_click", {
			solution_id: String(data.id),
			price_bucket: tracking.getPriceBucket(data.final_price ?? data.price),
			position: typeof index === "number" ? index + 1 : undefined,
			action_type: "add",
		});

		PayBox.alter({
			orderInfo: res.data,
			finalPrice: res.data.final_amount,
		});
	} catch (error) {
		console.error("Order failed:", error);
	} finally {
		isOrder.value = false;
	}
};

onBeforeUnmount(() => {
	impressionTracker?.reset?.();
});
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	overflow-wrap: anywhere;
}

.section-title {
	margin: 0 0 24px;
	color: #333;
	font-size: 24px;
	font-weight: 500;
	line-height: 1.3;
}

.solution-service {
	font-family: "Source Han Serif CN", "Source Han Serif CN";

	&__grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 18px;
		margin: 0;
		padding: 0;
		list-style: none;
	}
}

.solution-service-card {
	display: flex;
	align-items: stretch;
	min-width: 0;
	padding: 12px;
	border: 1px solid #f2f2f2;
	border-radius: 8px;
	background: #ffffff;
	cursor: pointer;
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease;

	&:hover {
		transform: translateY(-2px);
		box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
	}

	&__image {
		flex: 0 0 auto;
		width: 158px;
		aspect-ratio: 1;
		border-radius: 6px;
		object-fit: cover;
		background: #f3f3f3;
	}

	&__poster {
		display: block;
		width: 100%;
		aspect-ratio: 686 / 866;
		object-fit: cover;
		background: #f3f3f3;
	}

	&__content {
		display: flex;
		flex: 1;
		min-width: 0;
		flex-direction: column;
		padding: 4px 8px 2px 16px;
	}

	&__name {
		margin: 0;
		color: #333333;
		font-size: 18px;
		line-height: 1.35;
		@include line-clamp(1);
	}

	&__detail {
		min-width: 0;
		max-width: 406px;
		height: 66px;
		margin: 8px 0 10px;
		font-family: "Source Han Serif CN", "Source Han Serif CN";
		color: #585858;
		font-size: 14px;
		font-weight: 400;
		line-height: 22px;
		text-align: left;
		font-style: normal;
		text-transform: none;
		@include line-clamp(3);
	}

	&__original {
		margin: 0 0 6px;
		color: #999999;
		font-size: 13px;
		line-height: 1.4;
	}

	&__footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		margin-top: auto;
	}

	&__price {
		display: flex;
		align-items: center;
		width: 120px;
		height: 31px;
		min-width: 0;
		color: #de2828;

		img {
			width: 20px;
			height: 20px;
			margin-right: 4px;
		}

		span {
			font-family: "Source Han Serif CN", "Source Han Serif CN";
			font-size: 24px;
			font-weight: 700;
			line-height: 1;
			text-align: left;
			font-style: normal;
			text-transform: none;
		}
	}

	&__btn {
		flex: 0 0 auto;
		position: relative;
		width: 22px;
		min-width: 22px;
		height: 22px;
		padding: 0;
		border: 0;
		border-radius: 50%;
		background: #333333;
		color: #ffffff;
		cursor: pointer;

		&:hover {
			opacity: 0.7;
		}
	}

	&__plus {
		position: absolute;
		left: 50%;
		top: 50%;
		width: 10px;
		height: 10px;
		transform: translate(-50%, -50%);

		&::before,
		&::after {
			content: "";
			position: absolute;
			left: 50%;
			top: 50%;
			background: #ffffff;
			border-radius: 1px;
			transform: translate(-50%, -50%);
		}

		&::before {
			width: 10px;
			height: 2px;
		}

		&::after {
			width: 2px;
			height: 10px;
		}
	}
}

.solution-service--area {
	.solution-service__grid {
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 24px;
	}

	.solution-service-card {
		display: block;
		padding: 0;
		border: 0;
		border-radius: 8px;
		background: transparent;
		overflow: hidden;
	}
}

@media (max-width: 640px) {
	.solution-service {
		&__grid {
			grid-template-columns: 1fr;
		}
	}

	.solution-service-card {
		flex-wrap: wrap;

		&__image {
			width: 100%;
			aspect-ratio: 16 / 9;
		}

		&__content {
			padding: 14px 2px 2px;
		}
	}

	.solution-service--area {
		.solution-service__grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
}

@media (max-width: 420px) {
	.solution-service--area {
		.solution-service__grid {
			grid-template-columns: 1fr;
		}
	}
}
</style>
